import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { datadogRum } from '@datadog/browser-rum';

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const { t } = useTranslation();

  // Map backend error messages to translation keys
  const getTranslatedError = useCallback((errorMessage: string): string => {
    const errorMap: Record<string, string> = {
      'Invalid credentials': t('login.errors.invalidCredentials'),
      'Missing email or password': t('login.errors.missingFields'),
      'Too many login attempts. Please try again later.': t('login.errors.tooManyAttempts'),
      'Network error': t('login.errors.networkError'),
    };
    return errorMap[errorMessage] || t('login.errors.generic');
  }, [t]);

  useEffect(() => {
    datadogRum.startView({ name: 'Login Page' });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      setError(getTranslatedError(message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900">
      {/* Accent bar */}
      <div className="h-2 bg-indigo-600" />

      <div className="container mx-auto p-8">
        <div className="mx-auto max-w-sm">
          {/* Header spacing */}
          <div className="py-6 text-center" />

          {/* Card */}
          <div className="rounded shadow bg-white">
            {/* Header */}
            <div className="border-b py-4 text-center text-xl font-bold uppercase tracking-widest text-black">
              <div className="flex items-center justify-center space-x-3">
                <img src="/logo512.png" alt="Logo" className="h-14 w-14" />
                <span>{t('login.welcomeBack')}</span>
              </div>
            </div>

            {/* Form */}
            <form className="bg-gray-50 px-10 py-10" onSubmit={handleSubmit}>
              {error && (
                <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="mb-4">
                <input
                  className="w-full rounded-md border border-gray-300 p-3 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  name="email"
                  type="email"
                  placeholder={t('login.email')}
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="mb-6 relative">
                <input
                  className="w-full rounded-md border border-gray-300 p-3 pr-10 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="**************"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                  aria-pressed={showPassword}
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-5 w-5" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10.584 10.587A3 3 0 0012 15a3 3 0 002.121-.879M9.88 9.88A3 3 0 0115 12" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.62 6.62A9.956 9.956 0 0112 5c4.478 0 8.269 2.943 9.543 7a10.45 10.45 0 01-4.133 5.21M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.269-2.943-9.543-7a10.448 10.448 0 012.223-3.592" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-5 w-5" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>

              <div className="flex">
                <button
                  className="w-full rounded-md bg-indigo-600 p-4 text-sm font-bold uppercase tracking-wider text-white transition hover:bg-indigo-700 disabled:opacity-60"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? t('login.signingIn') : t('login.login')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}; 